<template>
  <el-container class="layout">
    <el-aside width="220px" class="aside">
      <div class="aside-wrapper">
        <div class="brand">
          <div class="brand-row">
            <img :src="logoUrl" class="brand-logo">
            <div class="brand-title">{{ $store.state.settings.system_name || 'PsyScale' }}</div>
          </div>
          <div class="brand-subtitle">{{ $store.state.settings.system_subtitle || '诊所测评系统' }}</div>
        </div>

        <el-menu :default-active="activePath" class="menu" router>
          <el-menu-item index="/app/visitors">
            <i class="el-icon-user"></i>
            <span slot="title">来访者</span>
          </el-menu-item>
          <el-menu-item index="/app/assessments">
            <i class="el-icon-document"></i>
            <span slot="title">测评记录</span>
          </el-menu-item>
          <el-menu-item v-if="canManageScales" index="/app/scales">
            <i class="el-icon-setting"></i>
            <span slot="title">量表管理</span>
          </el-menu-item>
          <el-menu-item v-if="canManageScales" index="/app/relations">
            <i class="el-icon-connection"></i>
            <span slot="title">协作管理</span>
          </el-menu-item>
          <el-menu-item v-if="isSuperuser" index="/app/settings">
            <i class="el-icon-s-operation"></i>
            <span slot="title">系统设置</span>
          </el-menu-item>
        </el-menu>
      </div>
    </el-aside>

    <el-container class="main-container">
      <el-header class="header">
        <div class="header-left">
          <div class="header-title">{{ pageTitle }}</div>
        </div>
        <div class="header-right">
          <el-button 
            type="text" 
            :icon="isDark ? 'el-icon-sunny' : 'el-icon-moon'" 
            @click="toggleTheme"
            style="font-size: 20px; margin-right: 16px;"
          ></el-button>
          
          <el-dropdown @command="handleCommand" trigger="click">
            <span class="el-dropdown-link" style="cursor: pointer; display: flex; align-items: center; color: var(--text-primary);">
              {{ $store.state.me ? ($store.state.me.first_name || $store.state.me.username) : '用户' }}
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile" icon="el-icon-user">个人中心</el-dropdown-item>
              <el-dropdown-item command="logout" icon="el-icon-switch-button" divided>退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </el-header>
      <el-main class="main">
        <router-view></router-view>
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
export default {
  name: 'AppLayout',
  data() {
    return {
      isDark: false,
      lastClickTime: 0
    }
  },
  created() {
    const saved = localStorage.getItem('theme')
    if (saved === 'dark') {
      this.isDark = true
      document.documentElement.classList.add('dark')
    }
  },
  computed: {
    activePath() {
      const p = this.$route.path
      if (p.startsWith('/app/assessments')) return '/app/assessments'
      if (p.startsWith('/app/scales')) return '/app/scales'
      if (p.startsWith('/app/settings')) return '/app/settings'
      if (p.startsWith('/app/relations')) return '/app/relations'
      if (p.startsWith('/app/profile')) return '' // No active menu for profile
      return '/app/visitors'
    },
    logoUrl() {
      const s = this.$store.state.settings
      if (s && s.system_logo) {
        return s.system_logo
      }
      // 回退到默认 Logo (确保即使没有 favicon 也能显示一个图标)
      return require('@/assets/logo.png')
    },
    pageTitle() {
      const p = this.$route.path
      if (p.startsWith('/app/assessments')) return '测评记录'
      if (p.startsWith('/app/scales')) return '量表管理'
      if (p.startsWith('/app/settings')) return '系统设置'
      if (p.startsWith('/app/relations')) return '协作管理'
      if (p.startsWith('/app/profile')) return '个人中心'
      return '来访者'
    },
    canManageScales() {
      const me = this.$store.state.me || {}
      return me.is_superuser || me.role === 'admin'
    },
    isSuperuser() {
      const me = this.$store.state.me || {}
      return me.is_superuser
    },
  },
  methods: {
    handleCommand(command) {
      if (command === 'logout') {
        this.logout()
      } else if (command === 'profile') {
        this.$router.push('/app/profile')
      }
    },
    toggleTheme() {
      const now = Date.now()
      if (now - this.lastClickTime < 500) return
      this.lastClickTime = now
      
      this.isDark = !this.isDark
      if (this.isDark) {
        document.documentElement.classList.add('dark')
        localStorage.setItem('theme', 'dark')
      } else {
        document.documentElement.classList.remove('dark')
        localStorage.setItem('theme', 'light')
      }
    },
    logout() {
      this.$store.dispatch('logout')
      this.$router.replace('/login')
    },
  },
}
</script>

<style scoped>
.layout {
  height: 100vh; /* Changed from min-height: 100vh to height: 100vh to prevent full page scroll */
  background: var(--bg-body);
  overflow: hidden; /* Hide overflow on the main container */
}
.aside {
  background: var(--aside-bg);
  border-right: 1px solid var(--border-color);
  height: 100vh; /* Ensure aside takes full height */
  overflow: hidden; /* Prevent scroll on the aside container */
}
.aside-wrapper {
  height: 100%;
  overflow-y: auto; /* Enable scroll inside the aside content if needed */
  display: flex;
  flex-direction: column;
}
.brand {
  padding: 18px 16px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0; /* Prevent brand from shrinking */
}
.brand-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.brand-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
}
.brand-title {
  font-size: 18px;
  font-weight: 800;
  color: var(--text-primary);
}
.brand-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
}
.menu {
  border-right: none;
  flex: 1; /* Make menu take remaining space */
  overflow-y: auto; /* Enable scroll for menu items */
}
.main-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--header-bg);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0; /* Prevent header from shrinking */
}
.header-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}
.main {
  padding: 18px;
  overflow-y: auto; /* Enable scroll only for the main content area */
  flex: 1;
}
</style>
